import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiImage } from "react-icons/fi";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Wrapper = styled.div<{ $aspectRatio?: string }>`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border-radius: inherit;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || "auto"};
`;

const Placeholder = styled.div<{ $loaded: boolean }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.surfaceAlt} 25%,
    ${({ theme }) => theme.colors.background.surface} 50%,
    ${({ theme }) => theme.colors.background.surfaceAlt} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  opacity: ${({ $loaded }) => ($loaded ? 0 : 1)};
  transition: opacity 0.4s ease;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Img = styled.img<{ $loaded: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.4s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }
`;

const FallbackIcon = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.5;
`;

interface SafeImageProps {
  src?: string;
  alt?: string;
  aspectRatio?: string;
  fallbackIcon?: React.ReactNode;
  className?: string;
}

const SafeImage = ({
  src,
  alt,
  aspectRatio,
  fallbackIcon = <FiImage aria-hidden="true" />,
  className,
}: SafeImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  if (errored || !src) {
    return (
      <Wrapper $aspectRatio={aspectRatio}>
        <FallbackIcon>{fallbackIcon}</FallbackIcon>
      </Wrapper>
    );
  }

  return (
    <Wrapper $aspectRatio={aspectRatio} className={className}>
      <Placeholder $loaded={loaded} />
      <Img
        src={src}
        alt={alt || ""}
        loading="lazy"
        $loaded={loaded}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
      />
    </Wrapper>
  );
};

export default SafeImage;
